import { motion } from 'framer-motion'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  TrendingUp,
  History,
  House,
  LogOut,
  Settings,
  Wallet,
  Repeat,
  type LucideIcon
} from 'lucide-react'
import WalletManager from '@/services/wallet/wallet-manager'
import { getAppVersion, isSwapEnabled } from '@/config/app'

const list = {
  visible: {
    opacity: 1,
    transition: {
      when: 'beforeChildren',
      staggerChildren: 0.1
    }
  },
  hidden: {
    opacity: 0,
    transition: {
      when: 'afterChildren'
    }
  }
}

const item = {
  visible: { opacity: 1, y: 0 },
  hidden: { opacity: 0, y: 20 }
}

type NavItem = {
  icon: LucideIcon
  labelKey: string
  path: string
}

const Sidebar = ({
  isOpen,
  setIsOpen
}: {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
}) => {
  const { t } = useTranslation()
  const location = useLocation()
  const walletManager = WalletManager.getInstance()
  const handleLogout = () => {
    walletManager.lock()
    sessionStorage.removeItem('wallet_privatekey')
    sessionStorage.removeItem('react_wallet_password')
    localStorage.setItem('wallet_requires_auth', 'true')
    window.location.href = '/'
  }

  const navItems: NavItem[] = [
    {
      path: '/dashboard',
      icon: House,
      labelKey: 'sidebar.dashboard'
    },
    { path: '/assets', icon: Wallet, labelKey: 'sidebar.assets' },
    {
      path: '/history',
      icon: History,
      labelKey: 'sidebar.history'
    },
    /*     {
      path: '/trading',
      icon: ChartCandlestick,
      labelKey: 'sidebar.trading'
    }, */
    {
      path: '/explorer',
      icon: TrendingUp,
      labelKey: 'sidebar.explore'
    },
    ...(isSwapEnabled()
      ? [
          {
            path: '/swap',
            icon: Repeat,
            labelKey: 'swap.title'
          }
        ]
      : []),
    {
      path: '/settings',
      icon: Settings,
      labelKey: 'sidebar.settings'
    }
  ]

  return (
    <aside
      className={`
          fixed top-0 left-0 h-full bg-card border-r border-border-base text-text-main z-40
          transition-transform duration-300 ease-in-out
          w-64 shadow-lg
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
        `}
    >
      <div className="flex flex-col h-full">
        {/* Sidebar Header */}
        <div className="p-3.75 flex gap-3 items-center">
          <img
            src="/assets/omd-wallet.svg"
            alt="OMD Wallet"
            className="w-12 h-12 mt-1"
          />
          <div className="flex flex-col">
            <p className="font-bold text-text-primary mt-1 text-[20px] ">
              OMD Wallet
            </p>
          </div>
        </div>

        {/* Navigation Links */}
        <nav className="flex-1 p-4">
          <motion.ul
            variants={list}
            className="space-y-2 h-full flex flex-col"
            initial="hidden"
            animate="visible"
          >
            {navItems.map((itemObj) => {
              const Icon = itemObj.icon
              const key = itemObj.path
              return (
                <motion.li key={key} variants={item}>
                  <Link
                    to={itemObj.path ?? '/dashboard'}
                    onClick={() => setIsOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      location.pathname === itemObj.path
                        ? 'primary-gradient-border-pseudo shadow-sm font-bold'
                        : 'hover:dark:bg-primary/10 hover:dark:text-primary hover:bg-accent/10 hover:text-tertiary'
                    }`}
                  >
                    <Icon
                      className={`w-5 h-5 ${
                        location.pathname === itemObj.path
                          ? 'icon-gradient'
                          : ''
                      }`}
                    />
                    <span
                      className={
                        location.pathname === itemObj.path
                          ? 'primary-gradient-text'
                          : ''
                      }
                    >
                      {t(itemObj.labelKey)}
                    </span>
                  </Link>
                </motion.li>
              )
            })}
          </motion.ul>
        </nav>
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 w-full px-8 py-8 text-accent"
        >
          <LogOut className="text-accent h-5 w-5" />
          <p className="text-accent text-sm font-bold">
            {t('settings.logout')}
          </p>
        </button>
        <p className="text-xs text-text-secondary p-3">
          v{getAppVersion()} OMDWallet
        </p>
      </div>
    </aside>
  )
}

export default Sidebar
